// src/utils/dateUtils.js

/**
 * Format a given date string in 'YYYY-MM-DD' format to a readable format.
 * @param {string} dateString - The date string in 'YYYY-MM-DD' format.
 * @returns {string} - The formatted date or an empty string if invalid.
 */
export function formatDate(dateString) {
    if (!dateString) return ''; // Return empty string if date is not provided

    const [year, month, day] = dateString.split('-');

    if (!year || year === '0000') return ''; // Invalid year
    if (month === '00' && day === '00') {
        return `${year}`; // Only show the year
    } else if (month === '00') {
        return `${year}`; // Invalid month, only show year
    } else if (day === '00') {
        const date = new Date(year, month - 1); // JavaScript months are 0-based
        const formatter = new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' });
        return formatter.format(date);
    } else {
        const date = new Date(dateString);
        const isValidDate = date instanceof Date && !isNaN(date);
        if (isValidDate) {
            const formatter = new Intl.DateTimeFormat('nl-NL', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            return formatter.format(date);
        } else {
            console.warn(`Invalid date detected: ${dateString}`);
            return ''; // Return empty string for invalid date
        }
    }
}

const JAVANESE_MONTH_NAMES = [
    'Sura',
    'Sapar',
    'Mulud',
    'Bakda Mulud',
    'Jumadilawal',
    'Jumadilakir',
    'Rejeb',
    'Ruwah',
    'Pasa',
    'Sawal',
    'Dulkangidah',
    'Besar'
];

const JAVANESE_WINDU_NAMES = ['Alip', 'Ehe', 'Jimawal', 'Je', 'Dal', 'Be', 'Wawu', 'Jimakir'];

const JAVANESE_PASARAN = [
    { name: 'Legi', neptu: 5 },
    { name: 'Pahing', neptu: 9 },
    { name: 'Pon', neptu: 7 },
    { name: 'Wage', neptu: 4 },
    { name: 'Kliwon', neptu: 8 }
];

const JAVANESE_WUKU_NAMES = [
    'Sinta',
    'Landep',
    'Wukir',
    'Kurantil',
    'Tolu',
    'Gumbreg',
    'Warigalit',
    'Warigagung',
    'Julungwangi',
    'Sungsang',
    'Galungan',
    'Kuningan',
    'Langkir',
    'Mandasiya',
    'Julungpujud',
    'Pahang',
    'Kuruwelut',
    'Marakeh',
    'Tambir',
    'Medangkungan',
    'Maktal',
    'Wuye',
    'Manahil',
    'Prangbakat',
    'Bala',
    'Wugu',
    'Wayang',
    'Kulawu',
    'Dukut',
    'Watugunung'
];

const JAVANESE_DAY_NAMES = [
    'Ahad',
    'Senin',
    'Selasa',
    'Rebo',
    'Kemis',
    'Jemuwah',
    'Setu'
];

const DAY_NEPTU = [5, 4, 3, 7, 8, 6, 9]; // Ahad=5, Senin=4, Selasa=3, Rebo=7, Kemis=8, Jemuwah=6, Setu=9

const JAVANESE_EPOCH = Date.UTC(1633, 6, 8); // 1 Sura 1555 AJ = 8 July 1633 CE
const PASARAN_EPOCH = Date.UTC(2008, 4, 7);
const PASARAN_EPOCH_INDEX = 4; // Kliwon

const KURUP_DAYS = 42524;

const WINDU_PATTERN = [
    354, // Alip
    355, // Ehe
    354, // Jimawal
    354, // Je
    355, // Dal
    354, // Be
    354, // Wawu
    355  // Jimakir
];

const WINDU_DAYS = WINDU_PATTERN.reduce((a, b) => a + b, 0);

const MONTH_LENGTHS_COMMON = [
    30, 29, 30, 29, 30, 29,
    30, 29, 30, 29, 30, 29
];

const MONTH_LENGTHS_LEAP = [
    30, 29, 30, 29, 30, 29,
    30, 29, 30, 29, 30, 30
];

function isValidJavaneseInputDate(dateString) {
    if (!dateString || dateString === '0000-00-00') return false;

    // Strip time component if present
    const cleanDate = dateString.split('T')[0];
    const [year, month, day] = cleanDate.split('-').map(Number);
    if (!year || !month || !day) return false;

    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year
        && date.getUTCMonth() === month - 1
        && date.getUTCDate() === day;
}

function createUtcDate(dateString) {
    // Strip time component if present
    const cleanDate = dateString.split('T')[0];
    const [year, month, day] = cleanDate.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}

function getWukuName(targetDate) {
    const jan1_2016 = new Date(Date.UTC(2016, 0, 1));
    const daysSinceJan1_2016 = Math.floor((targetDate - jan1_2016) / 86400000);
    const cycleDay = ((daysSinceJan1_2016 % 210) + 33 + 210) % 210;
    return JAVANESE_WUKU_NAMES[Math.floor(cycleDay / 7)];
}

function getJavaneseDateParts(targetDate) {
    const daysSinceEpoch = Math.floor((targetDate.getTime() - JAVANESE_EPOCH) / 86400000);

    // Consume kurup cycles first (15 windu, with a 1-day reduction in the final year).
    const kurupCycles = Math.floor(daysSinceEpoch / KURUP_DAYS);
    let dayInKurup = ((daysSinceEpoch % KURUP_DAYS) + KURUP_DAYS) % KURUP_DAYS;

    // Then consume windu cycles within the current kurup.
    let winduInKurup = 0;
    for (; winduInKurup < 15; winduInKurup += 1) {
        const winduDays = winduInKurup === 14 ? WINDU_DAYS - 1 : WINDU_DAYS;
        if (dayInKurup < winduDays) break;
        dayInKurup -= winduDays;
    }

    // Then consume years in the windu.
    let winduYear = 0;
    for (; winduYear < WINDU_PATTERN.length; winduYear += 1) {
        let yearDays = WINDU_PATTERN[winduYear];
        if (winduInKurup === 14 && winduYear === 7) {
            yearDays -= 1;
        }
        if (dayInKurup < yearDays) break;
        dayInKurup -= yearDays;
    }

    const isKurupShortYear = winduInKurup === 14 && winduYear === 7;
    const isLeap = WINDU_PATTERN[winduYear] === 355 && !isKurupShortYear;
    const monthLengths = isLeap ? MONTH_LENGTHS_LEAP : MONTH_LENGTHS_COMMON;

    let month = 0;
    let dayOfYear = dayInKurup;
    for (; month < monthLengths.length; month += 1) {
        const monthDays = monthLengths[month];
        if (dayOfYear < monthDays) break;
        dayOfYear -= monthDays;
    }

    const day = dayOfYear + 1;
    const year = 1555 + (kurupCycles * 120) + (winduInKurup * 8) + winduYear;

    return {
        day,
        month,
        year,
        winduYear,
        isLeap
    };
}

export function convertGregorianToJavanese(dateString) {
    if (!isValidJavaneseInputDate(dateString)) return null;

    const targetDate = createUtcDate(dateString);
    const { day, month, year, winduYear } = getJavaneseDateParts(targetDate);
    const daysSincePasaranEpoch = Math.floor((targetDate.getTime() - PASARAN_EPOCH) / 86400000);
    const pasaranIndex = ((PASARAN_EPOCH_INDEX + daysSincePasaranEpoch) % 5 + 5) % 5;
    const pasaran = JAVANESE_PASARAN[pasaranIndex];
    const wuku = getWukuName(targetDate);
    const warsa = JAVANESE_WINDU_NAMES[((year % 8) + 8) % 8];

    // Calculate weton (day name + pasaran)
    const dayOfWeek = targetDate.getUTCDay();
    const dayName = JAVANESE_DAY_NAMES[dayOfWeek];
    const weton = `${dayName} ${pasaran.name}`;

    // Calculate neptu (combined numeric value)
    const dayNeptu = DAY_NEPTU[dayOfWeek];
    const pasaranNeptu = pasaran.neptu;
    const neptu = dayNeptu + pasaranNeptu;

    return {
        day,
        month,
        monthName: JAVANESE_MONTH_NAMES[month],
        year,
        winduYear,
        winduName: JAVANESE_WINDU_NAMES[winduYear],
        warsa,
        pasaran,
        wuku,
        dayName,
        weton,
        neptu,
        formattedDate: `${day} ${JAVANESE_MONTH_NAMES[month]} ${year}`,
        formattedDateWithWeton: `${day} ${JAVANESE_MONTH_NAMES[month]} ${year}, ${weton}`
    };
}

export function formatJavaneseDate(dateString) {
    return convertGregorianToJavanese(dateString)?.formattedDate || '';
}

export function formatJavaneseDateWithWeton(dateString) {
    return convertGregorianToJavanese(dateString)?.formattedDateWithWeton || '';
}

/**
 * Format a given date string in 'YYYY-MM-DD' format to a short format.
 * Outputs like "15-12-1998".
 * @param {string} dateString - The date string in 'YYYY-MM-DD' format.
 * @returns {string} - The formatted date or an empty string if invalid.
 */
export function formatShortDate(dateString) {
    if (!dateString || dateString === '0000-00-00') return '';

    // Remove any unwanted time components
    dateString = dateString.split('T')[0];

    const parts = dateString.split('-');
    if (parts.length === 3) {
        const [year, month, day] = parts;
        if (day === '00' && month === '00') {
            return year;
        } else if (day === '00') {
            return `${month}-${year}`;
        } else if (month === '00') {
            return year;
        } else {
            return `${day}-${month}-${year}`;
        }
    }

    return dateString; // fallback for unexpected formats
}

/**
 * Format years based on birthdate, altbirthdate, deathdate, burialdate, and living status.
 * @param {string} birthdate - The birthdate in 'YYYY-MM-DD' format.
 * @param {string} altbirthdate - The alternative birthdate (baptism date) in 'YYYY-MM-DD' format.
 * @param {string} deathdate - The death date in 'YYYY-MM-DD' format.
 * @param {string} burialdate - The burial date in 'YYYY-MM-DD' format.
 * @param {number} living - The living status, 1 if alive, 0 otherwise.
 * @returns {string} The formatted years string.
 */
export function formatYears(birthdate, altbirthdate, deathdate, burialdate, living, locale = 'nl') {
    const strings = {
        nl: {
            yearWord: () => 'jaar',
            living: 'in leven',
            bornIn: 'Geboren in',
            diedIn: 'Overleden in',
            isLiving: 'In leven',
            deceasedDateUnknown: 'Overleden, datum onbekend',
            birthAndDeathUnknown: 'Geboorte- en overlijdensdatum onbekend',
        },
        en: {
            yearWord: (n) => n === 1 ? 'year' : 'years',
            living: 'living',
            bornIn: 'Born in',
            diedIn: 'Died in',
            isLiving: 'Living',
            deceasedDateUnknown: 'Deceased, date unknown',
            birthAndDeathUnknown: 'Birth and death date unknown',
        },
        id: {
            yearWord: () => 'tahun',
            living: 'masih hidup',
            bornIn: 'Lahir',
            diedIn: 'Wafat',
            isLiving: 'Masih hidup',
            deceasedDateUnknown: 'Wafat, tanggal tidak diketahui',
            birthAndDeathUnknown: 'Tanggal lahir dan wafat tidak diketahui',
        },
    };
    const t = strings[locale] || strings.nl;

    // Helper function to check for valid dates
    const isValidDate = (date) => date && date !== '0000-00-00';

    // Extract only the year from a date string
    const extractYear = (date) => {
        if (!date || date === '0000-00-00') return null;
        const [year] = date.split('-');
        return year !== '0000' ? year : null;
    };

    // Get birth and death years
    const birthYear = isValidDate(birthdate) ? extractYear(birthdate) : extractYear(altbirthdate);
    const deathYear = isValidDate(deathdate) ? extractYear(deathdate) : extractYear(burialdate);

    // Format the years
    if (birthYear && deathYear) {
        const lifespan = deathYear - birthYear;
        return `${birthYear} – ${deathYear} (${lifespan} ${t.yearWord(lifespan)})`;
    }

    if (birthYear && !deathYear) {
        if (living === 1) {
            return `${birthYear} – (${t.living})`;
        } else {
            return `${t.bornIn} ${birthYear}`;
        }
    }

    if (!birthYear && deathYear) {
        return `${t.diedIn} ${deathYear}`;
    }

    if (!birthYear && !deathYear) {
        if (living === 1) {
            return t.isLiving;
        } else {
            return t.deceasedDateUnknown;
        }
    }

    return t.birthAndDeathUnknown;
}

/**
 * Formats a given date relative to the current date, providing a human-readable time difference 
 * (e.g., "3 days ago," "2 months ago"). Uses a fallback of "Zojuist" for dates within seconds.
 * 
 * @param {string|Date} date - The date to format, either as a Date object or in a format that can be parsed by `Date`.
 * @returns {string} A localized relative time string (e.g., "5 dagen geleden" for Dutch).
 */
export function formatRelativeDate(date, locale = 'nl') {
    const fallback = { nl: 'Onbekend', en: 'Unknown', id: 'Tidak diketahui' };
    const justNow = { nl: 'Zojuist', en: 'Just now', id: 'Baru saja' };

    if (!date) return fallback[locale] || fallback.nl;

    const now = new Date();
    const pastDate = new Date(date);
    const diffInSeconds = (now - pastDate) / 1000;

    const timeIntervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'week', seconds: 604800 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 },
        { label: 'second', seconds: 1 },
    ];

    const rtfLocale = locale === 'id' ? 'id' : locale === 'en' ? 'en' : 'nl';
    for (const interval of timeIntervals) {
        const timePassed = Math.floor(diffInSeconds / interval.seconds);
        if (timePassed >= 1) {
            const rtf = new Intl.RelativeTimeFormat(rtfLocale, { numeric: 'auto' });
            return rtf.format(-timePassed, interval.label);
        }
    }

    return justNow[locale] || justNow.nl;
}

/**
 * Extract the most complete Date object from a date string (YYYY-MM-DD, YYYY-MM, YYYY).
 * Used for sorting dates with varying levels of precision.
 * @param {string} dateString - The date string in 'YYYY-MM-DD' format (with possible '00' for month/day).
 * @returns {Date|null} - A Date object for sorting, or null if no valid date parts are available.
 */
export function extractSortableDate(dateString) {
    if (!dateString || dateString === '0000-00-00') return null;

    const [year, month, day] = dateString.split('-');

    if (year && month && day && day !== '00') {
        // Full date available (YYYY-MM-DD)
        return new Date(`${year}-${month}-${day}`);
    } else if (year && month && month !== '00') {
        // Partial date available (YYYY-MM)
        return new Date(`${year}-${month}-01`); // Default to the first day of the month
    } else if (year) {
        // Year only available (YYYY)
        return new Date(`${year}-01-01`); // Default to January 1st of the year
    } else {
        return null;
    }
}
